import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Pagination from "../pagination/pagination";
import Filter from "../Filter";
import searchIcon from "../../assets/images/search.svg";
import downArrow from "../../assets/images/down-arrow.svg";
import upArrow from "../../assets/images/up-arrow.svg";

const DataTable = ({
    data = [],
    columns = [],
    searchable = true,
    searchPlaceholder = "Search...",
    itemsPerPage = 20,
    isLoading = false,
    noDataMessage = "No records found",
    paginator,
    onSort = () => {},
    onSearch = () => {},
    onPageChange = () => {},
    title,
    filterOptions = [],
    onFilterApply = () => {},
    initialFilters = {},
    onSectionSearch = () => {},
    pageCount,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [filteredData, setFilteredData] = useState([]);
    const [sortField, setSortField] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");
    const [filters, setFilters] = useState(initialFilters || {});

    useEffect(() => {
        if (!data || data.length === 0) {
            setFilteredData([]);
            return;
        }

        if (searchTerm.trim() === "") {
            setFilteredData(data);
            return;
        }

        const filtered = data?.filter((item) => {
            return columns.some((column) => {
                if (!column.searchable) return false;

                const keys = column.accessor ? column.accessor.split(".") : [];
                let value = item;

                for (const key of keys) {
                    if (value === null || value === undefined) return false;
                    value = value[key];
                }

                if (value === null || value === undefined) return false;
                return String(value)
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase());
            });
        });

        setFilteredData(filtered);
        setCurrentPage(1);
    }, [searchTerm, data, columns]);

    useEffect(() => {
        setFilters(initialFilters || {});
    }, [initialFilters]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);

        if (onPageChange) {
            onPageChange(pageNumber);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        onSearch(value);
    };

    const handleSort = (accessor) => {
        let newOrder;

        if (sortField !== accessor) {
            newOrder = "desc";
        } else {
            newOrder = sortOrder === "desc" ? "asc" : "desc";
        }

        setSortField(accessor);
        setSortOrder(newOrder);
        onSort(accessor, newOrder);
    };

    const getValueFromAccessor = (object, accessor) => {
        if (!accessor) return null;

        const keys = accessor.split(".");
        let value = object;

        for (const key of keys) {
            if (value === null || value === undefined) return null;
            value = value[key];
        }

        return value;
    };

    const useServerPagination = !!paginator;
    const currentItems = useServerPagination
        ? data
        : filteredData?.slice(indexOfFirstItem, indexOfLastItem);

    const handleFilterApply = (appliedFilters) => {
        setFilters(appliedFilters);
        if (onFilterApply) {
            onFilterApply(appliedFilters);
        }
    };

    return (
        <TableContainer>
            <TableHeaderWrapper>
                <TableHeaderTitle>
                    {title} <Count>{pageCount}</Count>
                </TableHeaderTitle>

                <TableHeaderActions>
                    {searchable && (
                        <SearchContainer>
                            <img src={searchIcon} alt="search" />
                            <input
                                type="text"
                                placeholder={searchPlaceholder}
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                        </SearchContainer>
                    )}

                    {filterOptions.length > 0 && (
                        <Filter
                            filterOptions={filterOptions}
                            onApply={handleFilterApply}
                            initialFilters={filters}
                            onSectionSearch={onSectionSearch}
                        />
                    )}
                </TableHeaderActions>
            </TableHeaderWrapper>

            <TableContentWrapper>
                <>
                    <StyledTable>
                        <TableHead>
                            <TableRow>
                                {columns.map((column, index) => (
                                    <TableHeader
                                        key={index}
                                        width={column.width || "auto"}
                                    >
                                        <HeaderContent>
                                            <span>{column.header}</span>
                                            {column.sortable && (
                                                <SortIcon
                                                    onClick={() =>
                                                        handleSort(
                                                            column.accessor
                                                        )
                                                    }
                                                    active={
                                                        sortField ===
                                                        column.accessor
                                                    }
                                                    direction={sortOrder}
                                                >
                                                    <img
                                                        src={
                                                            sortField ===
                                                            column.accessor
                                                                ? sortOrder ===
                                                                  "desc"
                                                                    ? upArrow
                                                                    : downArrow
                                                                : downArrow
                                                        }
                                                        alt="sort"
                                                    />
                                                </SortIcon>
                                            )}
                                        </HeaderContent>
                                    </TableHeader>
                                ))}
                            </TableRow>
                        </TableHead>
                        {isLoading ? (
                            <LoadingContainer>
                                <LoadingText>Loading...</LoadingText>
                            </LoadingContainer>
                        ) : filteredData.length === 0 ? (
                            <NoDataContainer>
                                <NoDataText>{noDataMessage}</NoDataText>
                            </NoDataContainer>
                        ) : (
                            <TableBody>
                                {currentItems.map((row, rowIndex) => {
                                    const slNo = rowIndex + 1;

                                    return (
                                        <TableRow key={rowIndex}>
                                            {columns.map((column, colIndex) => {
                                                if (column.header === "SL No") {
                                                    return (
                                                        <TableCell
                                                            key={colIndex}
                                                        >
                                                            {slNo}
                                                        </TableCell>
                                                    );
                                                }

                                                return (
                                                    <TableCell key={colIndex}>
                                                        {column.cell
                                                            ? column.cell(
                                                                  row,
                                                                  rowIndex
                                                              )
                                                            : getValueFromAccessor(
                                                                  row,
                                                                  column.accessor
                                                              )}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        )}
                    </StyledTable>
                </>
            </TableContentWrapper>

            <PaginationContainer>
                <Pagination
                    postsPerPage={itemsPerPage}
                    totalPosts={filteredData.length}
                    paginate={paginate}
                    currentPage={paginator?.current_page || currentPage}
                    currentPosts={currentItems}
                    hasNext={indexOfLastItem < filteredData.length}
                    paginator={paginator}
                />
            </PaginationContainer>
        </TableContainer>
    );
};

export default DataTable;

const TableContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    min-height: 720px;
    overflow: hidden;
`;

const TableHeaderWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
`;

const TableHeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const Count = styled.span`
    font-size: 14px;
    color: #522e92;
    font-weight: 600;
    background-color: #edf5fe;
    padding: 5px;
    border-radius: 8px;
    width: auto;
    height: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #9dcbff;
`;

const TableHeaderTitle = styled.h2`
    font-size: 24px;
    font-weight: 600;
    color: #522e92;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
`;

const SearchContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    border: 1px solid #e9ecef;
    border-radius: 5px;
    padding: 6px;
    display: flex;
    align-items: center;
    gap: 10px;
    background-color: #f5f7fa;
    color: #522e92;

    img {
        width: 16px;
        height: 16px;
    }

    input {
        border: none;
        outline: none;
        padding: 5px;
        border-radius: 5px;
        width: 240px;
        background-color: #f5f7fa;
        color: #522e92;
        font-size: 14px;
        font-weight: 400;

        ::placeholder {
            color: #522e92;
        }
    }
`;

const TableContentWrapper = styled.div`
    overflow-x: auto;
    width: 100%;
`;

const StyledTable = styled.table`
    width: 100%;
    min-width: 1100px;
    border-collapse: collapse;
    border-spacing: 0;
`;

const TableHead = styled.thead`
    background-color: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
    border-bottom: 1px solid #e3e8ef;
    &:last-child {
        border-bottom: none;
    }
    /* &:hover {
        background-color: #e3e8ef;
    } */
    height: 60px;
`;

const TableHeader = styled.th`
    padding: 15px;
    text-align: left;
    font-weight: 600;
    color: #522e92;
    font-size: 14px;
    width: ${(props) => props.width};
    white-space: nowrap;
    vertical-align: middle;
`;

const HeaderContent = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    color: #010101;
    font-weight: 500;
    font-size: 14px;
`;

const SortIcon = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
    position: relative;

    img {
        width: 18px;
        height: 18px;
        opacity: ${(props) => (props.active ? 1 : 0.7)};
        transition: opacity 0.3s ease-in-out;

        &:hover {
            opacity: 1;
        }
    }
`;

const TableCell = styled.td`
    padding: 15px;
    text-align: left;
    font-size: 15px;
    color: #7b6294;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
`;

const PaginationContainer = styled.div`
    margin-top: auto;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 15px 0;
    border-top: 1px solid #e3e8ef;
    background-color: #fff;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 300px;
    font-size: 16px;
    color: #505887;
    width: 100%;
    position: absolute;
    left: 0;
    right: 0;
`;

const LoadingText = styled.div`
    color: #505887;
    font-size: 16px;
    font-weight: 500;
`;

const NoDataContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 300px;
    font-size: 16px;
    color: #505887;
    width: 100%;
    position: absolute;
    left: 0;
    right: 0;
`;

const NoDataText = styled.div`
    color: #505887;
    font-size: 16px;
    font-weight: 500;
`;
